import { createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

const initialState = {
  // Main assessment data
  functionalSafetyAssessment: null,
  functionalSafetyAssessments: [],
  
  // Sub-assessments
  mttfAssessments: [],
  dcavgAssessments: [],
  ccfAssessments: [],
  hsrAssessments: [],
  
  // Stats (calculated scores)
  stats: null,
  
  // Static data
  ccfOptions: [],
  hsrOptions: [],
  
  // Loading states
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isLoadingOptions: false,
  isLoadingStats: false,
  
  // Error handling
  error: null,
  
  // UI states
  selectedAssessment: null,
  activeModal: null,
};

const functionalSafetySlice = createSlice({
  name: 'functionalSafety',
  initialState,
  reducers: {
    // Main assessment actions
    getFunctionalSafetyAssessment: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    getFunctionalSafetyAssessmentSuccess: (state, action) => {
      state.isLoading = false;
      const assessment = action.payload;

      state.functionalSafetyAssessment = assessment;
      state.functionalSafetyAssessments = assessment ? (Array.isArray(assessment) ? assessment : [assessment]) : [];

      // Extract and store sub-assessments if they exist
      if (assessment) {
        state.mttfAssessments = assessment.mttf_assessments || [];
        state.dcavgAssessments = assessment.dcavg_assessments || [];
        state.ccfAssessments = assessment.ccf_assessments || [];
        state.hsrAssessments = assessment.hsr_assessments || [];
      }
    },
    getFunctionalSafetyAssessmentFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
      toast.error('Failed to fetch Functional Safety Assessment', {
        autoClose: 2000,
        className: 'toast-error',
      });
    },

    // Upsert main assessment (create or update)
    upsertFunctionalSafetyAssessment: (state) => {
      state.isUpdating = true;
      state.error = null;
    },
    upsertFunctionalSafetyAssessmentSuccess: (state, action) => {
      state.isUpdating = false;
      state.functionalSafetyAssessment = action.payload;
      const index = state.functionalSafetyAssessments.findIndex(
        item => item.id === action.payload.id
      );
      if (index !== -1) {
        state.functionalSafetyAssessments[index] = action.payload;
      } else {
        state.functionalSafetyAssessments = [action.payload];
      }
      toast.success('Functional Safety Assessment saved successfully', {
        autoClose: 2000,
        className: 'toast-success',
      });
    },
    upsertFunctionalSafetyAssessmentFailure: (state, action) => {
      state.isUpdating = false;
      state.error = action.payload;
      toast.error('Failed to save Functional Safety Assessment', {
        autoClose: 2000,
        className: 'toast-error',
      });
    },

    // MTTF Assessment actions
    createMTTFAssessment: (state) => {
      state.isCreating = true;
      state.error = null;
    },
    createMTTFAssessmentSuccess: (state, action) => {
      state.isCreating = false;
      state.mttfAssessments = action.payload;
      
      // Also update the nested mttf_assessments in the main assessment object
      if (state.functionalSafetyAssessment) {
        state.functionalSafetyAssessment = {
          ...state.functionalSafetyAssessment,
          mttf_assessments: action.payload,
        };
      }
      
      toast.success('MTTF Assessment created successfully', {
        autoClose: 2000,
        className: 'toast-success',
      });
    },
    createMTTFAssessmentFailure: (state, action) => {
      state.isCreating = false;
      state.error = action.payload;
      toast.error('Failed to create MTTF Assessment', {
        autoClose: 2000,
        className: 'toast-error',
      });
    },

    updateMTTFAssessment: (state) => {
      state.isUpdating = true;
      state.error = null;
    },
    updateMTTFAssessmentSuccess: (state, action) => {
      state.isUpdating = false;
      state.mttfAssessments = action.payload;
      
      // Also update the nested mttf_assessments in the main assessment object
      if (state.functionalSafetyAssessment) {
        state.functionalSafetyAssessment = {
          ...state.functionalSafetyAssessment,
          mttf_assessments: action.payload,
        };
      }
      
      toast.success('MTTF Assessment updated successfully', {
        autoClose: 2000,
        className: 'toast-success',
      });
    },
    updateMTTFAssessmentFailure: (state, action) => {
      state.isUpdating = false;
      state.error = action.payload;
      toast.error('Failed to update MTTF Assessment', {
        autoClose: 2000,
        className: 'toast-error',
      });
    },

    deleteMTTFAssessment: (state) => {
      state.isDeleting = true;
      state.error = null;
    },
    deleteMTTFAssessmentSuccess: (state, action) => {
      state.isDeleting = false;
      state.mttfAssessments = action.payload;
      
      // Also update the nested mttf_assessments in the main assessment object
      if (state.functionalSafetyAssessment) {
        state.functionalSafetyAssessment = {
          ...state.functionalSafetyAssessment,
          mttf_assessments: action.payload,
        };
      }
      
      toast.success('MTTF Assessment deleted successfully', {
        autoClose: 2000,
        className: 'toast-success',
      });
    },
    deleteMTTFAssessmentFailure: (state, action) => {
      state.isDeleting = false;
      state.error = action.payload;
      toast.error('Failed to delete MTTF Assessment', {
        autoClose: 2000,
        className: 'toast-error',
      });
    },

    // DCavg Assessment actions
    createDCavgAssessment: (state) => {
      state.isCreating = true;
      state.error = null;
    },
    createDCavgAssessmentSuccess: (state, action) => {
      state.isCreating = false;
      state.dcavgAssessments = action.payload;
      
      // Also update the nested dcavg_assessments in the main assessment object
      if (state.functionalSafetyAssessment) {
        state.functionalSafetyAssessment = {
          ...state.functionalSafetyAssessment,
          dcavg_assessments: action.payload,
        };
      }
      
      toast.success('DCavg Assessment created successfully', {
        autoClose: 2000,
        className: 'toast-success',
      });
    },
    createDCavgAssessmentFailure: (state, action) => {
      state.isCreating = false;
      state.error = action.payload;
      toast.error('Failed to create DCavg Assessment', {
        autoClose: 2000,
        className: 'toast-error',
      });
    },

    updateDCavgAssessment: (state) => {
      state.isUpdating = true;
      state.error = null;
    },
    updateDCavgAssessmentSuccess: (state, action) => {
      state.isUpdating = false;
      state.dcavgAssessments = action.payload;
      
      // Also update the nested dcavg_assessments in the main assessment object
      if (state.functionalSafetyAssessment) {
        state.functionalSafetyAssessment = {
          ...state.functionalSafetyAssessment,
          dcavg_assessments: action.payload,
        };
      }
      
      toast.success('DCavg Assessment updated successfully', {
        autoClose: 2000,
        className: 'toast-success',
      });
    },
    updateDCavgAssessmentFailure: (state, action) => {
      state.isUpdating = false;
      state.error = action.payload;
      toast.error('Failed to update DCavg Assessment', {
        autoClose: 2000,
        className: 'toast-error',
      });
    },

    deleteDCavgAssessment: (state) => {
      state.isDeleting = true;
      state.error = null;
    },
    deleteDCavgAssessmentSuccess: (state, action) => {
      state.isDeleting = false;
      state.dcavgAssessments = action.payload;
      
      // Also update the nested dcavg_assessments in the main assessment object
      if (state.functionalSafetyAssessment) {
        state.functionalSafetyAssessment = {
          ...state.functionalSafetyAssessment,
          dcavg_assessments: action.payload,
        };
      }
      
      toast.success('DCavg Assessment deleted successfully', {
        autoClose: 2000,
        className: 'toast-success',
      });
    },
    deleteDCavgAssessmentFailure: (state, action) => {
      state.isDeleting = false;
      state.error = action.payload;
      toast.error('Failed to delete DCavg Assessment', {
        autoClose: 2000,
        className: 'toast-error',
      });
    },

    // CCF Assessment actions
    createCCFAssessment: (state) => {
      state.isCreating = true;
      state.error = null;
    },
    createCCFAssessmentSuccess: (state, action) => {
      state.isCreating = false;
      state.ccfAssessments = action.payload;
      
      // Also update the nested ccf_assessments in the main assessment object
      if (state.functionalSafetyAssessment) {
        state.functionalSafetyAssessment = {
          ...state.functionalSafetyAssessment,
          ccf_assessments: action.payload,
        };
      }
      
      toast.success('CCF Assessment created successfully', {
        autoClose: 2000,
        className: 'toast-success',
      });
    },
    createCCFAssessmentFailure: (state, action) => {
      state.isCreating = false;
      state.error = action.payload;
      toast.error('Failed to create CCF Assessment', {
        autoClose: 2000,
        className: 'toast-error',
      });
    },

    deleteCCFAssessment: (state) => {
      state.isDeleting = true;
      state.error = null;
    },
    deleteCCFAssessmentSuccess: (state, action) => {
      state.isDeleting = false;
      state.ccfAssessments = action.payload;
      
      // Also update the nested ccf_assessments in the main assessment object
      if (state.functionalSafetyAssessment) {
        state.functionalSafetyAssessment = {
          ...state.functionalSafetyAssessment,
          ccf_assessments: action.payload,
        };
      }
      
      toast.success('CCF Assessment deleted successfully', {
        autoClose: 2000,
        className: 'toast-success',
      });
    },
    deleteCCFAssessmentFailure: (state, action) => {
      state.isDeleting = false;
      state.error = action.payload;
      toast.error('Failed to delete CCF Assessment', {
        autoClose: 2000,
        className: 'toast-error',
      });
    },

    // HSR Assessment actions
    createHSRAssessment: (state) => {
      state.isCreating = true;
      state.error = null;
    },
    createHSRAssessmentSuccess: (state, action) => {
      state.isCreating = false;
      state.hsrAssessments = action.payload;
      
      // Also update the nested hsr_assessments in the main assessment object
      if (state.functionalSafetyAssessment) {
        state.functionalSafetyAssessment = {
          ...state.functionalSafetyAssessment,
          hsr_assessments: action.payload,
        };
      }
      
      toast.success('HSR Assessment created successfully', {
        autoClose: 2000,
        className: 'toast-success',
      });
    },
    createHSRAssessmentFailure: (state, action) => {
      state.isCreating = false;
      state.error = action.payload;
      toast.error('Failed to create HSR Assessment', {
        autoClose: 2000,
        className: 'toast-error',
      });
    },

    deleteHSRAssessment: (state) => {
      state.isDeleting = true;
      state.error = null;
    },
    deleteHSRAssessmentSuccess: (state, action) => {
      state.isDeleting = false;
      state.hsrAssessments = action.payload;
      
      // Also update the nested hsr_assessments in the main assessment object
      if (state.functionalSafetyAssessment) {
        state.functionalSafetyAssessment = {
          ...state.functionalSafetyAssessment,
          hsr_assessments: action.payload,
        };
      }
      
      toast.success('HSR Assessment deleted successfully', {
        autoClose: 2000,
        className: 'toast-success',
      });
    },
    deleteHSRAssessmentFailure: (state, action) => {
      state.isDeleting = false;
      state.error = action.payload;
      toast.error('Failed to delete HSR Assessment', {
        autoClose: 2000,
        className: 'toast-error',
      });
    },

    // CCF Options actions
    getCCFOptions: (state) => {
      state.isLoadingOptions = true;
      state.error = null;
    },
    getCCFOptionsSuccess: (state, action) => {
      state.isLoadingOptions = false;
      state.ccfOptions = action.payload;
    },
    getCCFOptionsFailure: (state, action) => {
      state.isLoadingOptions = false;
      state.error = action.payload;
      toast.error('Failed to fetch CCF Options', {
        autoClose: 2000,
        className: 'toast-error',
      });
    },

    // HSR Options actions
    getHSROptions: (state) => {
      state.isLoadingOptions = true;
      state.error = null;
    },
    getHSROptionsSuccess: (state, action) => {
      state.isLoadingOptions = false;
      state.hsrOptions = action.payload;
    },
    getHSROptionsFailure: (state, action) => {
      state.isLoadingOptions = false;
      state.error = action.payload;
      toast.error('Failed to fetch HSR Options', {
        autoClose: 2000,
        className: 'toast-error',
      });
    },

    // Stats actions
    getFunctionalSafetyStats: (state) => {
      state.isLoadingStats = true;
      state.error = null;
    },
    getFunctionalSafetyStatsSuccess: (state, action) => {
      state.isLoadingStats = false;
      state.stats = action.payload;
      // Also update stats in the main assessment if it exists
      if (state.functionalSafetyAssessment && state.functionalSafetyAssessment.id === action.payload.id) {
        state.functionalSafetyAssessment = {
          ...state.functionalSafetyAssessment,
          total_mttf_score: action.payload.total_mttf_score,
          total_dcavg_score: action.payload.total_dcavg_score,
          total_ccf_score: action.payload.total_ccf_score,
          total_hsr_score: action.payload.total_hsr_score,
        };
      }
    },
    getFunctionalSafetyStatsFailure: (state, action) => {
      state.isLoadingStats = false;
      state.error = action.payload;
      toast.error('Failed to fetch Functional Safety Stats', {
        autoClose: 2000,
        className: 'toast-error',
      });
    },

    // UI actions
    setSelectedAssessment: (state, action) => {
      state.selectedAssessment = action.payload;
    },
    setActiveModal: (state, action) => {
      state.activeModal = action.payload;
    },
    clearActiveModal: (state) => {
      state.activeModal = null;
    },

    // Error handling
    clearError: (state) => {
      state.error = null;
    },
    clearAll: (state) => {
      
      return {
        ...initialState,
        ccfOptions: state.ccfOptions, 
        hsrOptions: state.hsrOptions,
        stats: null,
      };
    },

    // Import Functional Safety Assessment
    importFunctionalSafety: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    importFunctionalSafetySuccess: (state, action) => {
      state.isLoading = false;
      toast.success(action.payload.message || 'Functional Safety Assessment imported successfully', {autoClose: 3000});
    },
    importFunctionalSafetyFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
      toast.error(action.payload?.error || 'Failed to import Functional Safety Assessment', {autoClose: 3000});
    },
  },
});

export const {
  // Main assessment
  getFunctionalSafetyAssessment,
  getFunctionalSafetyAssessmentSuccess,
  getFunctionalSafetyAssessmentFailure,
  upsertFunctionalSafetyAssessment,
  upsertFunctionalSafetyAssessmentSuccess,
  upsertFunctionalSafetyAssessmentFailure,
  
  
  

  // MTTF Assessment
  createMTTFAssessment,
  createMTTFAssessmentSuccess,
  createMTTFAssessmentFailure,
  updateMTTFAssessment,
  updateMTTFAssessmentSuccess,
  updateMTTFAssessmentFailure,
  deleteMTTFAssessment,
  deleteMTTFAssessmentSuccess,
  deleteMTTFAssessmentFailure,

  // DCavg Assessment
  createDCavgAssessment,
  createDCavgAssessmentSuccess,
  createDCavgAssessmentFailure,
  updateDCavgAssessment,
  updateDCavgAssessmentSuccess,
  updateDCavgAssessmentFailure,
  deleteDCavgAssessment,
  deleteDCavgAssessmentSuccess,
  deleteDCavgAssessmentFailure,

  // CCF Assessment
  createCCFAssessment,
  createCCFAssessmentSuccess,
  createCCFAssessmentFailure,
  deleteCCFAssessment,
  deleteCCFAssessmentSuccess,
  deleteCCFAssessmentFailure,

  // HSR Assessment
  createHSRAssessment,
  createHSRAssessmentSuccess,
  createHSRAssessmentFailure,
  deleteHSRAssessment,
  deleteHSRAssessmentSuccess,
  deleteHSRAssessmentFailure,

  // Options
  getCCFOptions,
  getCCFOptionsSuccess,
  getCCFOptionsFailure,
  getHSROptions,
  getHSROptionsSuccess,
  getHSROptionsFailure,

  // Stats
  getFunctionalSafetyStats,
  getFunctionalSafetyStatsSuccess,
  getFunctionalSafetyStatsFailure,

  // Import
  importFunctionalSafety,
  importFunctionalSafetySuccess,
  importFunctionalSafetyFailure,

  // UI
  setSelectedAssessment,
  setActiveModal,
  clearActiveModal,

  // Error handling
  clearError,
  clearAll,
} = functionalSafetySlice.actions;

export default functionalSafetySlice.reducer;
